import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail } from 'lucide-react';
import SEO from '../components/SEO';
import CrisisSupport from '../components/CrisisSupport';
import ScrollIndicator from '../components/ScrollIndicator';
import './AssessmentPage.css';

const Schools = () => {
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) entry.target.classList.add('revealed');
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });
    const els = document.querySelectorAll('.scroll-reveal');
    els.forEach((el) => observer.observe(el));
    return () => els.forEach((el) => observer.unobserve(el));
  }, []);

  return (
    <div className="page-assessment" data-testid="schools-page">
      <SEO
        title="School Wellbeing Hub | On-site Psychology for Schools | Clinical Minds Psychology"
        description="The School Wellbeing Hub embeds psychology services on-site in rural and regional schools. Assessments, support and staff consultation from Clinical Minds Psychology, Coffs Harbour."
        canonicalUrl="https://www.clinicalminds.net/schools"
      />
      <ScrollIndicator />

      <section id="main-content" className="interior-hero" data-testid="page-hero">
        <div className="container">
          <span className="section-label">Schools</span>
          <h1 className="heading-hero">The School Wellbeing Hub</h1>
        </div>
      </section>

      {/* Intro */}
      <section className="assess-section scroll-reveal" data-testid="schools-intro">
        <div className="container assess-narrow">
          <div className="assess-body">
            <p className="body-medium">
              Rural and regional schools know their students better than anyone. What they often can't access is timely psychology support, and families can wait a year or more for an assessment that would change how a child is taught and supported.
            </p>
            <p className="body-medium" style={{ marginTop: '1rem' }}>
              The School Wellbeing Hub is a partnership model from Clinical Minds Psychology that brings the psychologist to the school. Instead of referring families out and hoping, your school gets a regular, embedded psychology service on your own grounds, in a setting students already know and trust.
            </p>
          </div>
        </div>
      </section>

      {/* How the partnership works */}
      <section className="assess-section assess-alt scroll-reveal" data-testid="schools-how">
        <div className="container assess-narrow">
          <span className="section-label">The Model</span>
          <h2 className="heading-1">How the partnership works</h2>
          <div className="assess-body">
            <p className="body-medium">
              Your school partners with Clinical Minds Psychology and I build a regular on-site presence around your needs. That can include psychological assessment conducted at school, support for individual students, consultation with teachers and learning support staff, and plain-language feedback that turns assessment results into practical classroom strategies.
            </p>
            <p className="body-medium" style={{ marginTop: '1rem' }}>
              Because the service is embedded rather than external, there's no disconnect between what happens in the assessment room and what happens in the classroom. Teachers can raise concerns early, families don't have to travel, and reports are written for the people who will actually use them.
            </p>
          </div>
        </div>
      </section>

      {/* Assessment where students feel comfortable */}
      <section className="assess-section scroll-reveal" data-testid="schools-assessment">
        <div className="container assess-narrow">
          <span className="section-label">Assessment</span>
          <h2 className="heading-1">Assessment where students feel comfortable</h2>
          <div className="assess-body">
            <p className="body-medium">
              Assessment is at the heart of the Hub. I assess for autism, ADHD, FASD, specific learning disorders and intellectual disability, using recognised tools, on-site at your school.
            </p>
            <p className="body-medium" style={{ marginTop: '1rem' }}>
              My approach is neuroaffirming and strengths-based. An assessment isn't about finding what's wrong with a student. It's about understanding how they think and learn, and where the fit between the student and their environment can be improved. Every assessment ends with clear, usable recommendations for school and home, and reports can support learning adjustments, NDIS access and NCCD evidence requirements.
            </p>
          </div>
        </div>
      </section>

      {/* What your school gains */}
      <section className="assess-section assess-alt scroll-reveal" data-testid="schools-gains">
        <div className="container assess-narrow">
          <span className="section-label">Benefits</span>
          <h2 className="heading-1">What your school gains</h2>
          <div className="assess-body">
            <p className="body-medium">
              A predictable pathway for students who are struggling, instead of long external waitlists. Staff who can consult a psychologist about students they're concerned about. Families who can access assessment without travel, time off work, or navigating an unfamiliar clinic. And a wellbeing offering that sets your school apart.
            </p>
          </div>
        </div>
      </section>

      {/* Who it suits */}
      <section className="assess-section scroll-reveal" data-testid="schools-suits">
        <div className="container assess-narrow">
          <span className="section-label">Who It's For</span>
          <h2 className="heading-1">Who it suits</h2>
          <div className="assess-body">
            <p className="body-medium">
              The Hub is designed for rural and regional schools that want more than an occasional visiting service. It works best as an ongoing partnership, tailored to your school's size, needs and term calendar. I work with schools across the Mid North Coast and Northern Rivers, including Coffs Harbour, Port Macquarie and Ballina, through to the Gold Coast, and in western Victoria.
            </p>
          </div>
        </div>
      </section>

      {/* Start the conversation */}
      <section className="assess-section assess-alt scroll-reveal" data-testid="schools-start">
        <div className="container assess-narrow">
          <span className="section-label">Get Started</span>
          <h2 className="heading-1">Start the conversation</h2>
          <div className="assess-body">
            <p className="body-medium">
              Every partnership starts with a conversation about what your school needs. Contact Lachlan Beaton, Clinical Psychologist (Registrar), at{' '}
              <a href="mailto:contact@clinicalminds.net" className="assess-link">contact@clinicalminds.net</a> or{' '}
              <a href="tel:0266002710" className="assess-link">02 6600 2710</a>.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="assess-cta-section" data-testid="schools-cta">
        <div className="container">
          <div className="assess-cta-card">
            <div className="assess-cta-buttons">
              <Link to="/contact?enquiry=school" className="btn-primary" data-testid="schools-enquiry-btn">
                Enquire about a school partnership
              </Link>
              <a href="tel:0266002710" className="assess-phone-cta">
                <Phone size={18} /> 02 6600 2710
              </a>
            </div>
          </div>
        </div>
      </section>

      <CrisisSupport />
    </div>
  );
};

export default Schools;
